/* WorkoutPlayer — guided playthrough with Sora 2 demo videos + Swedish TTS cues.
 * Flow per exercise:
 *   1. Show demo video (loop) + Nova ropar "Nu kör vi {name}, {sets} set"
 *   2. For each set: countdown (work timer or rep cue) + rest timer
 *   3. Advance to next exercise
 *
 * Voice: browser SpeechSynthesis with Swedish female voice. */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Play, Pause, SkipForward, X, Loader2, Volume2, VolumeX } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pick the best available Swedish female voice
function pickSwedishVoice() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  // Prefer Swedish female
  const sv = voices.filter((v) => (v.lang || '').toLowerCase().startsWith('sv'));
  if (sv.length) {
    const fem = sv.find((v) => /female|kvinn|alva|elin|nora/i.test(v.name));
    return fem || sv[0];
  }
  // Fallback to any available
  return voices.find((v) => /female|samantha|alice|karen|moira/i.test(v.name)) || voices[0] || null;
}

function speak(text, voice, opts = {}) {
  if (!text || typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    if (voice) u.voice = voice;
    u.lang = (voice && voice.lang) || 'sv-SE';
    u.rate = opts.rate || 1.0;
    u.pitch = opts.pitch || 1.1;
    u.volume = opts.volume != null ? opts.volume : 1.0;
    window.speechSynthesis.speak(u);
  } catch {/**/}
}

export default function WorkoutPlayer({ workout, coachName = 'Nova', onClose, onComplete }) {
  const [exIdx, setExIdx] = useState(0);
  const [setIdx, setSetIdx] = useState(0);     // 0..(sets-1)
  const [phase, setPhase] = useState('demo');  // demo | work | rest | done
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(false);
  const [secs, setSecs] = useState(0);
  const [videoB64, setVideoB64] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoTaskId, setVideoTaskId] = useState(null);
  const voiceRef = useRef(null);
  const timerRef = useRef(null);

  const exercises = workout?.exercises || [];
  const current = exercises[exIdx];
  const totalSets = Number(current?.sets) || 3;
  // If reps are time-based (contains "sek" or "s") parse it. Otherwise 40s work default.
  const workSeconds = (() => {
    const r = String(current?.reps || '');
    const m = r.match(/(\d+)\s*(s|sek)/i);
    return m ? Number(m[1]) : 40;
  })();
  const restSeconds = Number(current?.rest_sec) || 30;

  // Load voice once available
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const load = () => { voiceRef.current = pickSwedishVoice(); };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { try { window.speechSynthesis.cancel(); } catch {/**/} };
  }, []);

  const say = useCallback((text, opts) => {
    if (muted) return;
    speak(text, voiceRef.current, opts);
  }, [muted]);

  // Fetch / generate video when entering demo phase for a new exercise
  useEffect(() => {
    if (!current || phase !== 'demo') return;
    let cancelled = false;
    setVideoB64(null);
    setVideoTaskId(null);
    (async () => {
      setVideoLoading(true);
      try {
        const r = await axios.post(`${API}/pt/exercise-video`, {
          name: current.name, description: current.how_to || '',
        }, { withCredentials: true });
        if (cancelled) return;
        if (r.data?.status === 'ready' && r.data?.video_b64) {
          setVideoB64(r.data.video_b64);
          setVideoLoading(false);
        } else {
          setVideoTaskId(r.data?.id);
        }
      } catch (e) {
        toast.error('Kunde inte hämta video — kör utan');
        setVideoLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [exIdx, phase, current]);

  // Poll for video readiness
  useEffect(() => {
    if (!videoTaskId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/pt/exercise-video/${videoTaskId}`, { withCredentials: true });
        if (cancelled) return;
        if (r.data?.status === 'ready' && r.data?.video_b64) {
          setVideoB64(r.data.video_b64);
          setVideoTaskId(null);
          setVideoLoading(false);
        } else if (r.data?.status === 'failed') {
          toast.info('Ingen video idag — vi kör med text 💪');
          setVideoTaskId(null);
          setVideoLoading(false);
        }
      } catch {/**/}
    };
    const id = setInterval(tick, 6000);
    tick();
    return () => { cancelled = true; clearInterval(id); };
  }, [videoTaskId]);

  // Intro voice on each new exercise (demo phase)
  useEffect(() => {
    if (!current || phase !== 'demo' || paused) return;
    say(`Nu kör vi ${current.name}. ${totalSets} set. ${current.cues?.[0] || ''}`);
    // Auto-advance to work after 8 seconds (give time to watch demo)
    timerRef.current = setTimeout(() => {
      if (!paused) {
        setPhase('work');
        setSecs(workSeconds);
        say(`Set ${setIdx + 1}. ${workSeconds} sekunder. Kom igen!`, { pitch: 1.15 });
      }
    }, 8000);
    return () => clearTimeout(timerRef.current);
  }, [exIdx, phase]);

  // Countdown for work/rest phases
  useEffect(() => {
    if (paused) return;
    if (phase !== 'work' && phase !== 'rest') return;
    if (secs <= 0) {
      // Phase end
      if (phase === 'work') {
        const isLastSet = setIdx + 1 >= totalSets;
        if (isLastSet) {
          // Move to next exercise
          const next = exIdx + 1;
          if (next >= exercises.length) {
            setPhase('done');
            say(`Snyggt jobbat! Du klarade hela passet. ${workout?.outro || ''}`);
            return;
          }
          say('Bra jobbat! Nästa övning.');
          setExIdx(next);
          setSetIdx(0);
          setPhase('demo');
        } else {
          say(`Bra! Vila ${restSeconds} sekunder.`);
          setPhase('rest');
          setSecs(restSeconds);
        }
      } else {
        // Rest done → next set
        const nextSet = setIdx + 1;
        setSetIdx(nextSet);
        setPhase('work');
        setSecs(workSeconds);
        say(`Set ${nextSet + 1}. Kom igen!`, { pitch: 1.15 });
      }
      return;
    }
    // Speak cues at key moments
    if (phase === 'work' && (secs === Math.floor(workSeconds / 2))) {
      say('Halvvägs! Du klarar det.');
    } else if (phase === 'work' && secs === 5) {
      say('Fem... fyra... tre... två... ett!');
    } else if (phase === 'rest' && secs === 3) {
      say('Snart igen.');
    }
    const t = setTimeout(() => setSecs((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secs, phase, paused]);

  const skipExercise = () => {
    const next = exIdx + 1;
    if (next >= exercises.length) {
      setPhase('done');
      return;
    }
    setExIdx(next);
    setSetIdx(0);
    setPhase('demo');
    setSecs(0);
  };

  const finish = async () => {
    if (workout?.id && onComplete) onComplete(workout.id);
    try { window.speechSynthesis?.cancel(); } catch {/**/}
    onClose?.();
  };

  if (!current) return null;

  const totalEx = exercises.length;
  const progressPct = Math.round(((exIdx + (phase === 'done' ? 1 : 0)) / totalEx) * 100);

  return (
    <div className="fixed inset-0 z-[110] bg-gradient-to-br from-amber-900 via-orange-900 to-rose-900 flex flex-col" data-testid="pt-player">
      {/* Top bar */}
      <div className="px-4 py-3 flex items-center justify-between text-white">
        <div className="text-xs font-extrabold uppercase tracking-widest opacity-90">
          {coachName} · övning {Math.min(exIdx + 1, totalEx)} / {totalEx}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setMuted(!muted)} data-testid="pt-mute" className="p-2 rounded-full bg-white/15 hover:bg-white/25" title={muted ? 'Slå på röst' : 'Stäng av röst'}>
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <button onClick={() => setPaused((p) => !p)} data-testid="pt-pause" className="p-2 rounded-full bg-white/15 hover:bg-white/25" title={paused ? 'Återuppta' : 'Pausa'}>
            {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>
          <button onClick={finish} data-testid="pt-quit" className="p-2 rounded-full bg-white/15 hover:bg-white/25" title="Avsluta">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="px-4">
        <div className="h-1.5 bg-white/15 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-300 to-rose-300 transition-all" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 gap-4 overflow-hidden">
        {phase === 'done' ? (
          <div className="text-center text-white space-y-3 max-w-md">
            <div className="text-6xl">🏆</div>
            <h2 className="text-3xl font-extrabold">Snyggt jobbat!</h2>
            <p className="text-base opacity-90 italic">{workout?.outro || 'Du klarade hela passet.'}</p>
            <button onClick={finish} data-testid="pt-player-done" className="mt-4 px-6 py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold shadow-lg">
              Logga passet 💪
            </button>
          </div>
        ) : (
          <>
            <div className="text-center text-white">
              <h2 className="text-3xl sm:text-4xl font-extrabold drop-shadow">{current.name}</h2>
              <p className="text-xs uppercase font-bold opacity-80 tracking-widest mt-1">
                {phase === 'demo' && 'Titta på formen'}
                {phase === 'work' && `Set ${setIdx + 1} av ${totalSets}`}
                {phase === 'rest' && 'Vila'}
              </p>
            </div>

            {/* Video area */}
            <div className="w-full max-w-lg aspect-video rounded-3xl overflow-hidden bg-black/40 border-4 border-white/20 shadow-2xl flex items-center justify-center">
              {videoB64 ? (
                <video
                  src={`data:video/mp4;base64,${videoB64}`}
                  autoPlay muted loop playsInline
                  className="w-full h-full object-cover"
                  data-testid="pt-demo-video"
                />
              ) : videoLoading ? (
                <div className="text-center text-white/80">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto" />
                  <p className="text-xs mt-2">Nova spelar in demon… (~1-2 min)</p>
                </div>
              ) : (
                <div className="text-center text-white p-6">
                  <div className="text-5xl">🎬</div>
                  <p className="text-sm mt-2 italic opacity-80">{current.how_to || 'Kör med fokus på formen'}</p>
                </div>
              )}
            </div>

            {/* Big timer when work/rest */}
            {(phase === 'work' || phase === 'rest') && (
              <div className={`text-7xl sm:text-8xl font-extrabold tabular-nums drop-shadow ${phase === 'rest' ? 'text-amber-200' : 'text-white'}`} data-testid="pt-timer">
                {secs}s
              </div>
            )}

            {/* Cue chip */}
            {current.cues?.length > 0 && phase !== 'rest' && (
              <div className="flex flex-wrap gap-1.5 justify-center max-w-md">
                {current.cues.slice(0, 2).map((c, i) => (
                  <span key={i} className="text-xs bg-white/20 text-white px-2.5 py-1 rounded-full font-bold backdrop-blur">{c}</span>
                ))}
              </div>
            )}

            {phase === 'demo' && (
              <button
                onClick={() => { setPhase('work'); setSecs(workSeconds); say(`Set 1. ${workSeconds} sekunder. Kör!`, { pitch: 1.15 }); }}
                data-testid="pt-start-now"
                className="px-6 py-2.5 rounded-2xl bg-white text-amber-900 font-extrabold shadow-lg hover:bg-amber-100"
              >
                Jag är redo — kör 🔥
              </button>
            )}

            <button
              onClick={skipExercise}
              data-testid="pt-skip"
              className="text-white/80 hover:text-white text-xs font-bold flex items-center gap-1 underline"
            >
              <SkipForward className="w-3 h-3" /> Hoppa till nästa övning
            </button>
          </>
        )}
      </div>
    </div>
  );
}
